import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { useLanguage } from "@/i18n/LanguageContext";
import { PRIVACY } from "@/data/privacy";
import { Seo } from "@/components/Seo";

export default function PrivacyPage() {
  const { slug } = useParams();
  const { lang, setLang, t } = useLanguage();
  const policy = PRIVACY[slug];
  if (!policy) return <Navigate to="/" replace />;

  const sections = policy[lang];

  return (
    <div className="grain min-h-screen bg-void text-white">
      <Seo
        title={`Privacy Policy — ${policy.app} | Fabio Comelli`}
        path={`/privacy/${policy.slug}`}
        description={lang === "it"
          ? `Informativa sulla privacy di ${policy.app}: quali dati raccoglie l'app, come vengono utilizzati, le terze parti coinvolte e i tuoi diritti.`
          : `Privacy policy for ${policy.app}: what data the app collects, how it is used, third parties involved and your rights.`}
      />
      <Navbar />
      <main className="px-6 pt-32 md:px-12" data-testid={`privacy-page-${slug}`}>
        <div className="mx-auto max-w-4xl">
          <Link to="/" data-testid="privacy-back-link" className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-muted2 transition-colors duration-300 hover:text-neon">
            <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
            {t.privacy.back}
          </Link>

          <Reveal className="mt-12">
            <div className="flex items-center gap-4">
              {policy.icon ? (
                <img src={policy.icon} alt={policy.app} className="h-14 w-14 rounded-2xl border border-white/15" data-testid="privacy-app-icon" />
              ) : (
                <ShieldCheck className="h-8 w-8 text-neon" />
              )}
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-muted2">{t.privacy.forApp}</p>
            </div>
            <h1 className="mt-4 font-display text-4xl font-extrabold uppercase tracking-tight sm:text-5xl" data-testid="privacy-title">
              {policy.app}
            </h1>
            <div className="mt-6 flex flex-wrap items-center gap-4">
              {policy.updated && (
                <span className="font-mono text-xs text-muted2" data-testid="privacy-updated">
                  {t.privacy.updated}: {policy.updated}
                </span>
              )}
              <button
                onClick={() => setLang(lang === "it" ? "en" : "it")}
                data-testid="privacy-lang-switch"
                className="rounded-full border border-white/20 px-4 py-1.5 font-mono text-[11px] uppercase tracking-widest text-muted2 transition-colors duration-300 hover:border-neon hover:text-neon"
              >
                {t.privacy.switchNote}
              </button>
            </div>
          </Reveal>

          <div className="mt-16 space-y-12 pb-28" data-testid="privacy-content">
            {sections.map((s) => (
              <Reveal key={s.h}>
                <h2 className="font-display text-xl font-bold tracking-tight text-neon md:text-2xl">{s.h}</h2>
                <div className="mt-4 space-y-3 text-sm leading-relaxed text-muted2 md:text-base">
                  {s.body.map((p, i) => (
                    <p key={i} className={p.startsWith("•") ? "pl-4" : ""}>{p}</p>
                  ))}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
